import { Component, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { UserService } from "../../../core/services/contenful/user.service";
import { AuthService } from "../../../core/services/auth.service";
import { UserModel } from "../../../core/models/entities/user.model";
import Constants from "src/app/core/constants/constants";
import { MailService } from "src/app/core/services/mail.service";
import { NgxSpinnerService } from "ngx-spinner";
import { ToastrService } from "ngx-toastr";

@Component({
    selector: "app-login",
    templateUrl: "./login.component.html",
    styleUrls: ["./login.component.scss"],
})
export class LoginComponent implements OnInit {
    loginForm!: FormGroup;

    codeSent = false;
    generatedCode!: string;
    codeExpiration!: number;
    nbSentCode: number = 0;

    constructor(
        private fb: FormBuilder,
        private router: Router,
        private userService: UserService,
        private authService: AuthService,
        private mailService: MailService,
        private spinner: NgxSpinnerService,
        private toastr: ToastrService
    ) {}

    ngOnInit(): void {
        this.loginForm = this.fb.group({
            email: ["", [Validators.required, Validators.email]],
            code: [
                "",
                [
                    Validators.required,
                    Validators.minLength(6),
                    Validators.maxLength(6),
                ],
            ],
            rememberMe: [false],
        });
    }

    async onSubmit(): Promise<void> {
        if (!this.codeSent) {
            await this.sendCodeByEmail();
        } else {
            this.verifyCode();
        }
    }

    private generateCode(): string {
        const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
        const digits = "0123456789";
        let code = "";

        for (let i = 0; i < 6; i++) {
            code +=
                i % 2 === 0
                    ? letters[Math.floor(Math.random() * letters.length)]
                    : digits[Math.floor(Math.random() * digits.length)];
        }

        return code;
    }

    async sendCodeByEmail(): Promise<void> {
        if (this.loginForm.get("email")?.invalid) {
            return;
        }
        if (this.nbSentCode >= 3) {
            this.toastr.error("Vous avez atteint le nombre maximum de tentatives. Veuillez réessayer plus tard.", Constants.ToastrTitle);
            this.backToEmail();
            return;
        }
        try {
            this.spinner.show();
            this.generatedCode = this.generateCode();
            const dateNow = Date.now();
            this.codeExpiration = dateNow + 2 * 60 * 1000;

            const templateParams = {
                to_email: this.loginForm.get("email")?.value,
                code: this.generatedCode,
                time: new Date(this.codeExpiration).toString(),
            };

            await this.mailService.sendMail(
                templateParams,
                Constants.loginTemplateIdKey
            );

            this.nbSentCode++;
            this.codeSent = true;
            this.loginForm.get("email")?.disable();
            this.loginForm
                .get("code")
                ?.setValidators([
                    Validators.required,
                    Validators.minLength(6),
                    Validators.maxLength(6),
                ]);
            this.loginForm.get("code")?.updateValueAndValidity();
            
            this.toastr.success("Code envoyé par mail.", Constants.ToastrTitle);
        } catch (error) {
            console.error("Erreur email", error);
            this.toastr.error("Une erreur est survenue lors de l'envoi du code par mail. Veuillez réessayer ou contacter l'administrateur.",Constants.ToastrTitle)
        } finally {
            this.spinner.hide();
        }
    }

    backToEmail() {
        this.codeSent = false;
        this.nbSentCode = 0;
        this.loginForm.get("email")?.enable();
        this.loginForm.get("code")?.clearValidators();
        this.loginForm.get("code")?.updateValueAndValidity();
    }

    private verifyCode(): void {
        if (Date.now() > this.codeExpiration) {
            this.toastr.error("Code expiré.", Constants.ToastrTitle);
            return;
        }

        const enteredCode = this.loginForm.value.code;

        if (enteredCode !== this.generatedCode) {
            this.toastr.error("Code invalide.", Constants.ToastrTitle);
            return;
        }

        this.checkUserExistsAndRedirect(this.loginForm.get("email")?.value);
    }

    private checkUserExistsAndRedirect(email: string) {
        this.spinner.show();
        this.userService.searchByEmail(email).subscribe({
            next: (user) => {
                let url = "/";
                if (user) {
                    this.authService.login(user, this.loginForm.value.rememberMe);
                    this.toastr.success("Connexion réussie.", Constants.ToastrTitle);
                } else {
                    const newUser = {
                        email: email,
                    } as UserModel;
                    this.authService.login(
                        newUser,
                        this.loginForm.value.rememberMe
                    );
                    url = "/signup";
                    this.toastr.warning("Veuillez compléter votre profil.", Constants.ToastrTitle);
                }

                this.spinner.hide();
                this.router.navigate([url]);
            },
            error: (error) => {
                this.spinner.hide();
                console.error("Erreur lors de la vérification de l'utilisateur", error);
                this.toastr.error(
                    "Une erreur est survenue lors de l'enregitrement de l'inscription. Veuillez réessayer ou contacter l'administrateur.",
                    Constants.ToastrTitle
                );
            }
        });
    }
}
